import { CliError, EXIT_INPUT, EXIT_USAGE } from "../common.js";

export type ConfigMissingSlot = "gateway" | "access" | "project" | "output";

export interface ScriptOutputApiErrorOptions {
  status?: number | null;
  code?: number | string | null;
  payload?: unknown;
  /**
   * Which env var was missing when fromEnv() failed — used by apiErrorToCli to
   * route the failure into a sanitized public error. Kept as a stable token
   * (not the raw env name) so apiErrorToCli doesn't need to parse messages.
   * "output" is the local-mode slot (SCRIPTCTL_OUTPUT_DIR missing or unwritable).
   */
  configMissing?: ConfigMissingSlot;
}

export class ScriptOutputApiError extends Error {
  readonly status: number | null;
  readonly code: number | string | null;
  readonly payload: unknown;
  readonly configMissing: ConfigMissingSlot | null;

  constructor(message: string, opts: ScriptOutputApiErrorOptions = {}) {
    super(message);
    this.name = "ScriptOutputApiError";
    this.status = opts.status ?? null;
    this.code = opts.code ?? null;
    this.payload = opts.payload;
    this.configMissing = opts.configMissing ?? null;
  }
}

export interface ReplaceScriptOptions {
  requestId: string;
  baseRevision: number;
  script: Record<string, unknown>;
  source?: string;
}

export interface ScriptSnapshot {
  projectGroupNo: string;
  script: Record<string, unknown>;
  revision: number;
  validationStatus?: string | null;
  validationSummary?: Record<string, unknown> | null;
  updatedAt?: string | null;
}

export interface PublishPatchOptions {
  baseRevision: number;
  old: Record<string, unknown>;
  edited: Record<string, unknown>;
  validation: {
    status: "valid";
    summary: Record<string, unknown>;
  };
}

/**
 * Incremental edit: publish the RFC6902 diff between the loaded script and the
 * locally-edited script, instead of re-uploading the whole document. `old` is
 * the script as loaded (the edit baseline), `edited` is the post-mutation
 * script. Implementations compute `compare(old, edited)` and send the patch.
 *
 * V1 backends have no incremental endpoint, so their implementation degrades to
 * a whole-document `replaceScript(edited)` — callers get the same observable
 * result (a new revision) regardless of which store is wired, so editing
 * usecases can always call `patchScript` without branching on the backend.
 */
export interface PatchScriptOptions {
  requestId: string;
  baseRevision: number;
  old: Record<string, unknown>;
  edited: Record<string, unknown>;
  source?: string;
}

export interface SaveValidationOptions {
  revision: number;
  validationStatus: string;
  validationSummary: Record<string, unknown>;
  issues: Array<Record<string, unknown>>;
}

/**
 * Abstract "published script storage" used by usecases. Two implementations:
 *   - RemoteScriptOutputStore: HTTP gateway (production)
 *   - LocalScriptOutputStore:  filesystem (local debugging)
 *
 * Implementations are selected by the `scriptOutputClient()` factory based on
 * `SCRIPTCTL_OUTPUT_MODE`. Usecase code only touches this interface, so the
 * mode switch is invisible above this layer.
 */
export interface ScriptOutputStore {
  /**
   * Stable identifier for the underlying store. For the remote backend this is
   * the gateway-side project group id; for the local backend it is the store
   * directory path. Used in artifact labels and "not found" error context.
   */
  readonly projectGroupNo: string;
  getScript(): Promise<Record<string, unknown> | null>;
  getRevision(): Promise<Record<string, unknown> | null>;
  replaceScript(opts: ReplaceScriptOptions): Promise<Record<string, unknown>>;
  /**
   * Apply an incremental edit. V2 stores send an RFC6902 PATCH; V1 stores fall
   * back to a whole-document replace. Both return the same revision envelope as
   * `replaceScript`, so editing usecases never branch on the backend version.
   */
  patchScript(opts: PatchScriptOptions): Promise<Record<string, unknown>>;
  saveValidationResult(opts: SaveValidationOptions): Promise<Record<string, unknown> | null>;
  /**
   * Best-effort write of the project group's `episode_count` (the column the
   * home page "我的项目" card reads). Optional: only the remote gateway store
   * implements it — the local store omits it. Callers MUST treat a rejection as
   * non-fatal; project-group metadata must never fail the primary script write.
   */
  updateEpisodeCount?(episodeCount: number): Promise<void>;
}

// Push the project group's episode count after a script write. Best-effort by
// design: the home-page episode_count is non-essential metadata, so a failure
// (auth, network, backend lacking the endpoint) is swallowed and never rolls
// back or surfaces above the primary script save. No-op on stores without the
// optional method (the local store).
export async function syncEpisodeCount(client: ScriptOutputStore, episodeCount: number): Promise<void> {
  if (!client.updateEpisodeCount) return;
  try {
    await client.updateEpisodeCount(episodeCount);
  } catch {
    // ponytail: best-effort metadata — keep it off agent-facing output.
  }
}

export type OutputMode = "local" | "remote";

/**
 * Single source of truth for output-target resolution. The CLI core is
 * UNBOUND: a bare invocation with no per-call flag and no `SCRIPTCTL_OUTPUT_MODE`
 * env resolves to `mode: null` (the caller then errors) rather than silently
 * defaulting to the remote gateway. "Where does this write land" must always be
 * an explicit decision — never inferred from ambient gateway credentials.
 *
 * Precedence: per-call flag (`--remote` / `--local`) > `SCRIPTCTL_OUTPUT_MODE`
 * env (an explicit binding) > a project group in scope (`SANDBOX_PROJECT_GROUP_NO`
 * env, which the sandbox injects, or a `--project-group-no` flag → infer remote)
 * > none.
 *
 * The project-group inference is what keeps the sandbox zero-config: the backend
 * already injects `SANDBOX_PROJECT_GROUP_NO`, so a bare command there resolves to
 * remote without any extra binding. A dev box without that var stays unbound. A
 * *typo* in `SCRIPTCTL_OUTPUT_MODE` is "explicit but broken" → it errors rather
 * than falling through to inference, so a misconfiguration isn't silently masked.
 *
 * Returns:
 *   - mode:      "local" | "remote" | null (null = unresolved → caller errors)
 *   - conflict:  both `--remote` and `--local` were passed (mutually exclusive)
 *
 * Pure (never throws). `resolveOutputModeOrThrow` wraps it with the CliError
 * contract so the conflict / no-target handling lives in exactly one place.
 */
export function resolveOutputMode(flags?: {
  remote?: unknown;
  local?: unknown;
  projectGroupNo?: unknown;
}): {
  mode: OutputMode | null;
  conflict: boolean;
} {
  const remoteFlag = Boolean(flags?.remote);
  const localFlag = Boolean(flags?.local);
  if (remoteFlag && localFlag) return { mode: null, conflict: true };
  if (remoteFlag) return { mode: "remote", conflict: false };
  if (localFlag) return { mode: "local", conflict: false };

  const raw = (process.env.SCRIPTCTL_OUTPUT_MODE ?? "").trim().toLowerCase();
  if (raw === "local" || raw === "remote") return { mode: raw, conflict: false };
  if (raw !== "") {
    // Unrecognized non-empty value (a typo like "loca"): explicit but broken —
    // error rather than silently routing anywhere (including via inference).
    return { mode: null, conflict: false };
  }

  // `SCRIPTCTL_OUTPUT_MODE` unset: infer remote when a project group is in scope.
  // The presence of a project group identifier is a strong, specific signal of
  // remote intent (the sandbox marker / an explicit `--project-group-no`), unlike
  // generic ambient gateway credentials which must never imply a target.
  const pgFlag = typeof flags?.projectGroupNo === "string" ? flags.projectGroupNo.trim() : "";
  const pgEnv = (process.env.SANDBOX_PROJECT_GROUP_NO ?? "").trim();
  if (pgFlag !== "" || pgEnv !== "") return { mode: "remote", conflict: false };
  return { mode: null, conflict: false };
}

/**
 * Resolve the store mode or throw the canonical CliError. Single source of truth
 * for the "where does a STORE write land" contract, shared by every store
 * builder (scriptOutputClient, parse's makeOutputStore, commandExport, …):
 *   - `--remote` + `--local`     → TARGET_FLAG_CONFLICT
 *   - nothing resolvable         → SCRIPT_NO_TARGET (the CLI is unbound; refuse
 *                                  to guess — names only public flags, never the
 *                                  env var, to keep the mode concept out of
 *                                  agent-facing output)
 * Returns a non-null OutputMode on success.
 */
export function resolveOutputModeOrThrow(flags?: {
  remote?: unknown;
  local?: unknown;
  projectGroupNo?: unknown;
}): OutputMode {
  const resolved = resolveOutputMode(flags);
  if (resolved.conflict) {
    throw new CliError("--remote and --local are mutually exclusive.", {
            exitCode: EXIT_USAGE,
            errorCode: "TARGET_FLAG_CONFLICT",
            hint: "Drop one of the two flags and retry.",
            data: { expected: ["exactly one of --remote (publish to the project DB) or --local (write a local store)"], actual: ["--remote and --local"] },
          });
  }
  if (resolved.mode === null) {
    throw new CliError("No output target was selected, so this command has nowhere to read or write.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_NO_TARGET",
            hint: "Pass `--script-path <file.json>` to read/write a local script file,",
            data: { expected: ["an explicit output target"], actual: ["no --script-path, no --remote/--local, and no bound default"] },
          });
  }
  return resolved.mode;
}
